<script setup>
import { ref, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import instagramClient from 'dashboard/api/channel/instagramClient';
import MetaIntegrationConfigAPI from 'dashboard/api/metaIntegrationConfig';
import Button from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();

const hasError = ref(false);
const errorStateMessage = ref('');
const errorStateDescription = ref('');
const isRequestingAuthorization = ref(false);
const isSavingConfig = ref(false);
const metaConfig = ref({
  app_id: '',
  app_secret: '',
  verify_token: '',
  api_version: 'v22.0',
  app_secret_configured: false,
  verify_token_configured: false,
});

const hasInstagramConfig = () =>
  metaConfig.value.app_id &&
  (metaConfig.value.app_secret || metaConfig.value.app_secret_configured) &&
  (metaConfig.value.verify_token || metaConfig.value.verify_token_configured);

const fetchMetaConfig = async () => {
  try {
    const { data } = await MetaIntegrationConfigAPI.get();
    const instagram = data.instagram || {};
    metaConfig.value = {
      app_id: instagram.app_id || '',
      app_secret: '',
      verify_token: '',
      api_version: instagram.api_version || 'v22.0',
      app_secret_configured: !!instagram.app_secret_configured,
      verify_token_configured: !!instagram.verify_token_configured,
    };
  } catch (_) {
    // The channel flow is only available to authorized account admins.
  }
};

const syncInstagramConfigToWindow = data => {
  const instagram = data.instagram || {};
  window.chatwootConfig = {
    ...window.chatwootConfig,
    instagramAppId: instagram.app_id || window.chatwootConfig?.instagramAppId,
    instagramApiVersion:
      instagram.api_version || window.chatwootConfig?.instagramApiVersion,
  };
};

const saveInstagramConfig = async () => {
  isSavingConfig.value = true;
  try {
    const { data } = await MetaIntegrationConfigAPI.updateConfig({
      instagram: metaConfig.value,
    });
    syncInstagramConfigToWindow(data);
    await fetchMetaConfig();
  } finally {
    isSavingConfig.value = false;
  }
};

onMounted(() => {
  fetchMetaConfig();
  const urlParams = new URLSearchParams(window.location.search);
  //  TODO: Handle error type
  // const errorType = urlParams.get('error_type');
  const errorCode = urlParams.get('code');
  const errorMessage = urlParams.get('error_message');

  if (errorMessage) {
    hasError.value = true;
    if (errorCode === '400') {
      errorStateMessage.value = errorMessage;
      errorStateDescription.value = t('INBOX_MGMT.ADD.INSTAGRAM.ERROR_AUTH');
    } else {
      errorStateMessage.value = t('INBOX_MGMT.ADD.INSTAGRAM.ERROR_MESSAGE');
      errorStateDescription.value = errorMessage;
    }
  }
  // User need to remove the error params from the url to avoid the error to be shown again after page reload, so that user can try again
  const cleanURL = window.location.pathname;
  window.history.replaceState({}, document.title, cleanURL);
});

const requestAuthorization = async () => {
  if (!hasInstagramConfig()) {
    hasError.value = true;
    errorStateMessage.value = 'Instagram is not configured for this account.';
    errorStateDescription.value =
      'Configure and save the Instagram App ID, App Secret and Verify Token here before connecting Instagram.';
    return;
  }
  isRequestingAuthorization.value = true;
  const response = await instagramClient.generateAuthorization();
  const {
    data: { url },
  } = response;

  window.location.href = url;
};
</script>

<template>
  <div class="h-full p-6 w-full max-w-full flex-shrink-0 flex-grow-0">
    <div class="flex flex-col items-center justify-start h-full text-center">
      <div v-if="hasError" class="max-w-lg mx-auto text-center">
        <h5>{{ errorStateMessage }}</h5>
        <p
          v-if="errorStateDescription"
          v-dompurify-html="errorStateDescription"
        />
      </div>
      <div
        v-else
        class="flex flex-col items-center justify-center w-full px-8 py-10 text-center rounded-2xl outline outline-1 outline-n-weak"
      >
        <h6 class="text-2xl font-medium">
          {{ $t('INBOX_MGMT.ADD.INSTAGRAM.CONNECT_YOUR_INSTAGRAM_PROFILE') }}
        </h6>
        <p class="py-6 text-sm text-n-slate-11">
          {{ $t('INBOX_MGMT.ADD.INSTAGRAM.HELP') }}
        </p>
        <form
          class="grid w-full max-w-lg gap-4 mb-8 text-left"
          @submit.prevent="saveInstagramConfig"
        >
          <label>
            App ID
            <input v-model="metaConfig.app_id" type="text" />
          </label>
          <label>
            App Secret
            <input
              v-model="metaConfig.app_secret"
              type="password"
              :placeholder="
                metaConfig.app_secret_configured
                  ? 'Ya configurado. Dejar vacío para conservarlo.'
                  : ''
              "
            />
          </label>
          <label>
            Verify Token
            <input
              v-model="metaConfig.verify_token"
              type="password"
              :placeholder="
                metaConfig.verify_token_configured
                  ? 'Ya configurado. Dejar vacío para conservarlo.'
                  : ''
              "
            />
          </label>
          <label>
            Version API
            <input
              v-model="metaConfig.api_version"
              type="text"
              placeholder="v22.0"
            />
          </label>
          <div class="flex justify-end">
            <Button
              type="submit"
              :is-loading="isSavingConfig"
              label="Guardar configuración"
            />
          </div>
        </form>
        <Button
          class="text-white !rounded-full !px-6 bg-gradient-to-r from-[#833AB4] via-[#FD1D1D] to-[#FCAF45]"
          lg
          icon="i-ri-instagram-line"
          :disabled="isRequestingAuthorization"
          :is-loading="isRequestingAuthorization"
          :label="$t('INBOX_MGMT.ADD.INSTAGRAM.CONTINUE_WITH_INSTAGRAM')"
          @click="requestAuthorization()"
        />
      </div>
    </div>
  </div>
</template>
